import { Box, Stack, Typography } from '@mui/material';
import { isEmpty } from 'lodash';
import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useLocalStorage } from 'react-use';

import { Accordion } from '@/components/Accordion/Accordion';
import { Button } from '@/components/form/baseInputs/Button';
import { Stars01Icon } from '@/components/icons/Stars01Icon';
import { InfoTooltip } from '@/components/poppers/Tooltip/InfoTooltip';
import { LoadingOverlay } from '@/components/progress/LoadingOverlay/LoadingOverlay';
import { LOCAL_STORAGE_KEYS } from '@/constants/localStorageKeys';
import {
  AI_ONBOARDING_MODAL_REVIEW_ALL_PAGE_SIZE,
  AI_ONBOARDING_MODAL_VARIANT_CONFIGS,
} from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModal.constants';
import {
  AIOnboardingModalVariant,
  OpenAIOnboardingModalProps,
} from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModal.types';
import {
  getSuggestionTypeDisplayText,
  orderSuggestionsToReview,
} from '@/modules/aiSuggestionReviewCard/AISuggestionReviewCard.utils';
import { AISuggestionReviewProcessingBanner } from '@/modules/aiSuggestionReviewCard/components/AISuggestionReviewProcessingBanner';
import { useAISuggestionReviewCardContext } from '@/modules/aiSuggestionReviewCard/context/aiSuggestionReviewCard.context';
import { AISuggestionReviewEntityRow } from '@/modules/aiSuggestionReviewCard/rows/AISuggestionReviewEntityRow';
import { AiSuggestionForReviewCard } from '@/modules/aiSuggestions/aiSuggestions.contracts';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { useIsHouseholdWorkflowTrackerInProgress } from '@/modules/workflowTrackers/hooks/useIsHouseholdWorkflowTrackerInProgress';
import { COLORS } from '@/styles/tokens/colors';
import { AiSuggestionKind, WorkflowTrackerKind } from '@/types/schema';
import { UnreachableError } from '@/utils/errors';
import { pluralize } from '@/utils/formatting/strings';
import { localStorageBooleanSerializer } from '@/utils/localStorageSerializers';

import { AISuggestionReviewCardProvider } from './context/AISuggestionReviewCard.provider';
import { AISuggestionReviewAssetRow } from './rows/AISuggestionReviewAssetRow';
import { AISuggestionReviewLiabilityRow } from './rows/AISuggestionReviewLiabilityRow';
import { AISuggestionReviewLifeInsuranceRow } from './rows/AISuggestionReviewLifeInsuranceRow';
import { AISuggestionReviewTestamentaryEntityRow } from './rows/AISuggestionReviewTestamentaryEntityRow';

const LIMIT_TO_SHOW = 3;

interface AISuggestionReviewCardProps {
  variant: AIOnboardingModalVariant;
  displayMode?: 'accordion' | 'expanded-list';
  suggestionDisplayText: {
    singular: string;
    plural: string;
  };
  onOpenAIOnboardingModal: (
    props: Pick<OpenAIOnboardingModalProps, 'initialSelectedSuggestion'>
  ) => void;
  /**
   * Optional externally-provided suggestions to render. When provided, the card
   * will skip internal polling and derive its state from these suggestions.
   */
  suggestions?: AiSuggestionForReviewCard[];
  /**
   * Suggestions needed only for resolving references from visible suggestions.
   * These are included in lookup maps but are not displayed unless they are
   * pending suggestions for this card's variant.
   */
  additionalReferenceSuggestions?: AiSuggestionForReviewCard[];
  /**
   * When false, suppresses the "Documents processing" banner within this card.
   * Defaults to true to preserve existing behavior.
   */
  showProcessingBanner?: boolean;
  /**
   * Optional callback invoked after a suggestion is successfully created.
   * Useful for refetching externally-provided suggestions.
   */
  onSuggestionCreated?: () => void;
}

function AISuggestionReviewCardInner({
  variant,
  displayMode = 'accordion',
  suggestionDisplayText,
  showProcessingBanner = true,
}: Omit<AISuggestionReviewCardProps, 'onOpenAIOnboardingModal'>) {
  const { possibleGrantors } = useHouseholdDetailsContext();

  const {
    reviewSuggestions,
    creatableSuggestions,
    onCreateAllClick,
    onReviewAllClick,
    isCreatingAll,
    principalNodesLoading,
  } = useAISuggestionReviewCardContext();

  const [showAll, setShowAll] = useState(false);
  const [accordionExpanded, setAccordionExpanded] = useLocalStorage<boolean>(
    LOCAL_STORAGE_KEYS.AI_SUGGESTION_REVIEW_ACCORDION_EXPANDED,
    true,
    localStorageBooleanSerializer
  );

  const isAIOnboardingWorkflowInProgress =
    useIsHouseholdWorkflowTrackerInProgress(
      WorkflowTrackerKind.DocumentExtraction
    );

  const isHouseholdRegenInProgress = useIsHouseholdWorkflowTrackerInProgress(
    WorkflowTrackerKind.AiSuggestionsV2HouseholdRegen
  );

  const suggestionKinds = useMemo(
    () => AI_ONBOARDING_MODAL_VARIANT_CONFIGS[variant].suggestionKinds,
    [variant]
  );

  const title = useMemo(() => {
    const count = reviewSuggestions.length;
    return `${pluralize({ count, singular: suggestionDisplayText.singular, plural: suggestionDisplayText.plural })} identified`;
  }, [reviewSuggestions, suggestionDisplayText]);

  const grantorIds = useMemo(
    () => (possibleGrantors ?? []).map((g) => g.id),
    [possibleGrantors]
  );

  const rows = useMemo(() => {
    const allRows = orderSuggestionsToReview(
      reviewSuggestions,
      variant,
      grantorIds
    );

    if (displayMode === 'expanded-list' || showAll) {
      return allRows;
    }

    return allRows.slice(0, LIMIT_TO_SHOW);
  }, [displayMode, grantorIds, reviewSuggestions, showAll, variant]);

  const handleCreateAll = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      onCreateAllClick();
    },
    [onCreateAllClick]
  );

  const handleReviewAll = useCallback(() => {
    onReviewAllClick();
  }, [onReviewAllClick]);

  const handleShowAll = useCallback(() => {
    // Expanding hundreds of rows inline isn't usable (or performant), so once
    // the list exceeds one review-modal page, route "Show all" into the
    // paginated review modal instead.
    if (reviewSuggestions.length > AI_ONBOARDING_MODAL_REVIEW_ALL_PAGE_SIZE) {
      onReviewAllClick();
      return;
    }

    setShowAll(true);
  }, [reviewSuggestions.length, onReviewAllClick]);

  const handleAccordionChange = useCallback(
    (expanded: boolean) => {
      setAccordionExpanded(expanded);
    },
    [setAccordionExpanded]
  );

  const hasSuggestions = !isEmpty(reviewSuggestions);
  const isExpandedList = displayMode === 'expanded-list';

  const suggestionRows = (
    <Stack direction="column" gap={1}>
      {rows.map((suggestion) => {
        switch (suggestion.kind) {
          case AiSuggestionKind.OnboardingEntity:
            return (
              <AISuggestionReviewEntityRow
                key={suggestion.id}
                suggestion={suggestion}
              />
            );
          case AiSuggestionKind.OnboardingTestamentaryEntity:
            return (
              <AISuggestionReviewTestamentaryEntityRow
                key={suggestion.id}
                suggestion={suggestion}
              />
            );
          case AiSuggestionKind.OnboardingIndividual:
          case AiSuggestionKind.OnboardingOrganization:
            return null;
          case AiSuggestionKind.Asset:
            return (
              <AISuggestionReviewAssetRow
                key={suggestion.id}
                suggestion={suggestion}
              />
            );
          case AiSuggestionKind.Liability:
            return (
              <AISuggestionReviewLiabilityRow
                key={suggestion.id}
                suggestion={suggestion}
              />
            );
          case AiSuggestionKind.LifeInsurance:
            return (
              <AISuggestionReviewLifeInsuranceRow
                key={suggestion.id}
                suggestion={suggestion}
              />
            );
          default:
            throw new UnreachableError({
              case: suggestion.kind,
              message: `Unsupported suggestion kind ${suggestion.kind}`,
            });
        }
      })}
    </Stack>
  );

  const suggestionActions = (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      mt={1.5}
    >
      {!isExpandedList &&
        !showAll &&
        reviewSuggestions.length > LIMIT_TO_SHOW && (
          <Button
            data-testid="show-all-suggestions-button"
            variant="transparent"
            size="xs"
            onClick={handleShowAll}
          >
            Show all ({reviewSuggestions.length})
          </Button>
        )}
      {reviewSuggestions.length > 1 && (
        <Stack direction="row" gap={1} ml="auto">
          {creatableSuggestions.length > 0 && (
            <Button
              data-testid="create-all-suggestions-button"
              variant="primary"
              size="xs"
              onClick={handleCreateAll}
              disabled={principalNodesLoading}
            >
              {isExpandedList
                ? 'Create all'
                : `Create all (${creatableSuggestions.length})`}
            </Button>
          )}
          <Button
            data-testid="review-all-suggestions-button"
            variant="secondary"
            size="xs"
            onClick={handleReviewAll}
            disabled={reviewSuggestions.length === 0}
          >
            {isExpandedList
              ? 'Review all'
              : `Review all (${reviewSuggestions.length})`}
          </Button>
        </Stack>
      )}
    </Stack>
  );

  return (
    <>
      {showProcessingBanner && isAIOnboardingWorkflowInProgress && (
        <Box mb={hasSuggestions ? 2 : 0}>
          <AISuggestionReviewProcessingBanner
            suggestionTypeText={getSuggestionTypeDisplayText(suggestionKinds)}
          />
        </Box>
      )}
      {showProcessingBanner &&
        !isAIOnboardingWorkflowInProgress &&
        isHouseholdRegenInProgress && (
          <Box mb={hasSuggestions ? 2 : 0}>
            <AISuggestionReviewProcessingBanner
              suggestionTypeText={getSuggestionTypeDisplayText(suggestionKinds)}
              isHouseholdRegen
            />
          </Box>
        )}
      {hasSuggestions && isExpandedList && (
        <Stack data-testid="ai-suggestion-review-card">
          {suggestionRows}
          {suggestionActions}
        </Stack>
      )}
      {hasSuggestions && !isExpandedList && (
        <Accordion
          variant="filled-teal"
          isDefaultExpanded={accordionExpanded}
          withDivider={false}
          leftAdornment={<Stars01Icon color={COLORS.NAVY['700']} />}
          title={
            <Stack direction="row" gap={1} alignItems="center">
              <Typography variant="title-s">{title}</Typography>
              <InfoTooltip
                title={`The following ${suggestionDisplayText.plural} appear in client documents but have not yet been created.`}
                iconProps={{ sx: { color: COLORS.NAVY['700'] } }}
              />
            </Stack>
          }
          onChange={handleAccordionChange}
          expandIconProps={{ color: COLORS.NAVY['700'] }}
          data-testid="ai-suggestion-review-card"
        >
          {suggestionRows}
          {suggestionActions}
        </Accordion>
      )}
      {isCreatingAll && (
        <LoadingOverlay
          open={isCreatingAll}
          updatingText="Creating suggestions..."
          data-testid="ai-suggestions-bulk-loading"
        />
      )}
    </>
  );
}

/**
 * Displays household-level AI suggestions of the given kinds that are ready
 * for review.
 */
export function AISuggestionReviewCard({
  variant,
  displayMode,
  suggestionDisplayText,
  onOpenAIOnboardingModal,
  suggestions,
  additionalReferenceSuggestions,
  showProcessingBanner = true,
  onSuggestionCreated,
}: AISuggestionReviewCardProps) {
  return (
    <AISuggestionReviewCardProvider
      variant={variant}
      onOpenAIOnboardingModal={onOpenAIOnboardingModal}
      suggestions={suggestions}
      additionalReferenceSuggestions={additionalReferenceSuggestions}
      onSuggestionCreated={onSuggestionCreated}
    >
      <AISuggestionReviewCardInner
        variant={variant}
        displayMode={displayMode}
        suggestionDisplayText={suggestionDisplayText}
        showProcessingBanner={showProcessingBanner}
      />
    </AISuggestionReviewCardProvider>
  );
}
